/**
 * SyPI initialization + release-version orchestration.
 *
 * Entry points: `sypiV2Init` (called from page-template on route mount),
 * `fetchSypi` (called on release-version change and rehydration),
 * `fetchAndLoadReleaseVersions` (populates the version dropdown).
 *
 * Uses `runAsyncAction` in `fetchHistoryConfig` — the pattern (dispatch
 * spinner, do work, dispatch stop-spinner, catch → reportError) fits cleanly
 * and lets the shared spinner/error scaffolding own the try/catch.
 */

import cloneDeep from "lodash/cloneDeep";
import { getStore } from "@store";
import sypiAdapter from "@features/sypi/adapters";
import { getCTIDOptions, getConfigurationDetails } from "@interfaces/config";
import { isEmptyObject } from "@utils/object-helpers";
import { givenTimeIsBeforeOther } from "@utils/time-helpers";
import { findMaxMinByKey } from "@utils/array-helpers";
import { getArrItemByKey } from "@utils/search-helpers";
import { runAsyncAction } from "@utils/runAsyncAction";
import {
  showSpinner,
  stopSpinner,
  clearChanges,
  closeDock,
  clearLog,
  setAlertDock,
} from "@actions/global";
import {
  loadReleaseVersions,
  setReleaseVersion,
  setInitialReleaseVersion,
  setDefaultReleaseVersion,
} from "@features/sypi/actions";
import { fetchSypiConfig, fetchAndLoadSchemaTemplate } from "./sypi-config";
import {
  mergeAndHandleVisibilitySettings,
} from "./sypi-config";
import {
  mergeLoggedChanges,
  releaseHasConfig,
} from "@features/sypi/_utils/sypi-interface-helpers";
import { dispatchErrors } from "./sypi-errors";

export const sypiV2Init = async (store = getStore()) => {
  getCTIDOptions(store);
  fetchAndLoadReleaseVersions(store);
  getConfigurationDetails(store);
  fetchHistoryConfig(store);
};

export const fetchSypi = async (
  release_version = "",
  fetchNew = false,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  const activeReleaseVersion = release_version
    ? release_version
    : store.getState().sypiSchema.activeReleaseVersion;
  const initialConfigCall =
    fetchNew || !store.getState().sypiNewView.rehydrated;
  let config =
    (initialConfigCall
      ? await fetchSypiConfig(activeReleaseVersion)
      : store.getState().sypiNewView.sypiConfig) || {};
  const { loggedChanges, changeDict } = store.getState().sypiNewView;
  if (initialConfigCall) {
    if (typeof config === "object") {
      config = config.sypi_config;
    } else if (typeof config === "string") {
      store.dispatch(
        setAlertDock({
          title: "Configuration not created yet",
          message: `No configuration exists yet for release version ${activeReleaseVersion}. Generate a blank configuration from schema default values, or import values from another client or release version, using the options below.`,
        })
      );
      config = {};
    }
  }
  let updatedConfig = cloneDeep(config);
  let setSypiInitial = true;
  let { configTemplate, formattedSchema } = await fetchAndLoadSchemaTemplate(
    activeReleaseVersion
  );
  let configTemplateCopy = cloneDeep(configTemplate);
  if (loggedChanges && loggedChanges.length) {
    if (config && !isEmptyObject(config)) {
      updatedConfig = mergeLoggedChanges(updatedConfig, changeDict);
      setSypiInitial = false;
    } else if (config && isEmptyObject(config)) {
      updatedConfig = mergeLoggedChanges(configTemplateCopy, changeDict);
      setSypiInitial = false;
    }
  }
  const mergedConfig = mergeAndHandleVisibilitySettings(
    configTemplate,
    updatedConfig,
    setSypiInitial
  );
  dispatchErrors(mergedConfig, formattedSchema, activeReleaseVersion);
  store.dispatch(stopSpinner());
};

/**
 * Checks whether the most recent scheduled change already went live and, if
 * so, refetches the config so the user sees the applied values.
 *
 * Adopts `runAsyncAction` for spinner + centralized error surface.
 */
export const fetchHistoryConfig = async (store = getStore()) => {
  await runAsyncAction(
    store,
    async () => {
      const historySet = await sypiAdapter.fetchHistoryConfig({ limit: 1 });
      const { configSchedules } = historySet || [];
      const lastScheduledConfig = configSchedules?.length
        ? configSchedules[0]
        : {};
      const { fetchInitialConfigTimeStamp } = store.getState().sypiNewView;
      const { activeReleaseVersion } = store.getState().sypiSchema;
      if (
        !isEmptyObject(lastScheduledConfig) &&
        givenTimeIsBeforeOther(
          fetchInitialConfigTimeStamp,
          lastScheduledConfig?.liveScheduleDate
        ) &&
        activeReleaseVersion === lastScheduledConfig?.release_version
      ) {
        store.dispatch(
          setAlertDock({
            title: "Scheduled change went live.",
            message: `Scheduled change '${lastScheduledConfig.name}' for release version ${lastScheduledConfig.release_version} recently went live and the config was updated with the scheduled values.`,
          })
        );
        await fetchSypi(activeReleaseVersion, true);
      }
    },
    { errorAction: "load", errorSubject: "history config" }
  );
};

export const fetchConfigReleaseVersions = async (ctid = "") =>
  await sypiAdapter.fetchSypiConfigReleaseVersions(null, ctid);

export const fetchAndLoadReleaseVersions = async (store = getStore()) => {
  store.dispatch(showSpinner());
  const configReleaseVersions = await fetchConfigReleaseVersions();
  let currentReleaseVersion = "",
    isActivedReleaseDisabled = false;
  let updatedReleaseVersions = [];
  if (configReleaseVersions && configReleaseVersions.length) {
    updatedReleaseVersions = configReleaseVersions.filter(
      (release_version) => !release_version.is_disable
    );
    const versionsWithConfig = updatedReleaseVersions.filter(releaseHasConfig);
    const activeReleaseVersion =
      store.getState()?.sypiSchema?.activeReleaseVersion;
    const activeReleaseVersionItem = activeReleaseVersion
      ? getArrItemByKey(
          "release_version",
          activeReleaseVersion,
          updatedReleaseVersions
        )
      : {};
    isActivedReleaseDisabled = activeReleaseVersionItem?.is_disable;
    // Default to the latest version that already has a config so the import
    // page doesn't auto-select an enabled-but-uncreated version and error.
    const activeReleaseVersionDetails = findMaxMinByKey(
      versionsWithConfig.length ? versionsWithConfig : updatedReleaseVersions,
      "release_version"
    );

    currentReleaseVersion =
      !isActivedReleaseDisabled &&
      activeReleaseVersion &&
      releaseHasConfig(activeReleaseVersionItem)
        ? activeReleaseVersion
        : !isEmptyObject(activeReleaseVersionDetails)
        ? activeReleaseVersionDetails.release_version
        : "";
    store.dispatch(setReleaseVersion(`${currentReleaseVersion}`));
    store.dispatch(setInitialReleaseVersion(`${currentReleaseVersion}`));
    store.dispatch(setDefaultReleaseVersion(`${currentReleaseVersion}`));
    store.dispatch(loadReleaseVersions(updatedReleaseVersions));
  }
  store.dispatch(stopSpinner());
  return { currentReleaseVersion, updatedReleaseVersions };
};

export const handleChangeReleaseVersion = async (
  release_version,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  // Release version needs to update before due to form state source of truth
  // See src/sypiV2/sypi-form-view.js or src/sypiV2/form-customizations/BlurForm.js
  // For more info
  store.dispatch(setReleaseVersion(release_version));
  store.dispatch(closeDock());
  store.dispatch(clearChanges());
  store.dispatch(clearLog());
  await fetchSypi(release_version, true);
  store.dispatch(stopSpinner());
};
